
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { PhoneIcon, EnvelopeIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import EnhancedSEO from '@/components/seo/EnhancedSEO';
import { OG_IMAGES } from '@/constants/seoImages';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { sanitizeTextContent } from '@/utils/sanitizeHtml';

const Contact = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    topic: 'general',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = 'First name must be at least 2 characters';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = 'Last name must be at least 2 characters';
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Phone validation (optional but must be valid if provided)
    if (formData.phone.trim()) {
      const phoneRegex = /^[\+]?[\s\.\-\(\)]*([0-9][\s\.\-\(\)]*){10,}$/;
      if (!phoneRegex.test(formData.phone.trim())) {
        newErrors.phone = 'Please enter a valid phone number';
      }
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    } else if (formData.subject.trim().length < 5) {
      newErrors.subject = 'Subject must be at least 5 characters';
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.trim().length > 2000) {
      newErrors.message = 'Message must be less than 2000 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Sanitize all inputs
      const sanitizedData = {
        first_name: sanitizeTextContent(formData.firstName.trim()),
        last_name: sanitizeTextContent(formData.lastName.trim()),
        email: formData.email.trim().toLowerCase(),
        phone: sanitizeTextContent(formData.phone.trim()),
        subject: sanitizeTextContent(formData.subject.trim()),
        message: sanitizeTextContent(formData.message.trim())
      };

      const params = new URLSearchParams(window.location.search);
      const referralCode = params.get('ref') || 
                          params.get('referral') || 
                          localStorage.getItem('referral_code') || 
                          '';
      
      const { data: resultData, error: dbError } = await supabase.rpc('insert_contact_submission', {
        p_first_name: sanitizedData.first_name,
        p_last_name: sanitizedData.last_name,
        p_email: sanitizedData.email,
        p_phone: sanitizedData.phone,
        p_subject: sanitizedData.subject,
        p_message: sanitizedData.message,
        p_topic: formData.topic,
        p_page_path: window.location.pathname,
        p_referrer: document.referrer || '',
        p_utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
        p_utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
        p_utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
        p_utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || '',
        p_utm_term: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
        p_source: 'website'
      });

      if (dbError) throw dbError;

      // Send email notification with context
      const emailParams = new URLSearchParams(window.location.search);
      const payload = {
        ...sanitizedData,
        topic: formData.topic,
        pagePath: window.location.pathname,
        referrer: document.referrer || '',
        utmSource: emailParams.get('utm_source') || sessionStorage.getItem('utm_source') || '',
        utmMedium: emailParams.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
        utmCampaign: emailParams.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
        utmContent: emailParams.get('utm_content') || sessionStorage.getItem('utm_content') || '',
        utmTerm: emailParams.get('utm_term') || sessionStorage.getItem('utm_term') || '',
        referralCode,
        submissionId: resultData,
        source: 'website'
      };

      const { data: emailResult, error: emailError } = await supabase.functions.invoke('send-contact-email', {
        body: payload
      });

      if (emailError) {
        console.error('Email sending error:', emailError);
        // Don't throw here - form was saved successfully
      } else {
        console.log('send-contact-email result:', emailResult);
      }

      toast({
        title: "Message Sent!",
        description: "Thank you for contacting us. We'll get back to you within 24 hours."
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        topic: 'general',
        message: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: PhoneIcon,
      title: "Phone",
      details: ["(989) 589-5385"],
      action: "Call Us"
    },
    {
      icon: EnvelopeIcon,
      title: "Email",
      details: ["support@seniorthrive.com", "We respond within 24 hours"],
      action: "Email Us"
    },
    {
      icon: MapPinIcon,
      title: "Address",
      details: ["5436 Via Carrizo", "Laguna Woods, CA 92637"],
      action: "Get Directions"
    }
  ];

  return (
    <>
      <EnhancedSEO 
        title="Contact SeniorThrive - We're Here to Help"
        description="Contact SeniorThrive for support, partnership inquiries, or questions about aging in place technology. We're here to help."
        keywords="contact senior care, senior wellness support, elderly services contact, senior community help"
        canonical="https://seniorthrive.com/contact"
        image={OG_IMAGES.contact}
        breadcrumbs={[
          { name: "Home", url: "https://seniorthrive.com/" },
          { name: "Contact", url: "https://seniorthrive.com/contact" }
        ]}
        localBusiness={{
          name: "SeniorThrive",
          address: {
            street: "5436 Via Carrizo",
            city: "Laguna Woods",
            state: "CA",
            postalCode: "92637",
            country: "US"
          },
          phone: "(989) 589-5385",
          email: "support@seniorthrive.com"
        }}
      />
      
      <div className="py-20 px-4">
        <div className="container mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-foreground mb-4">
              Contact Us
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We're here to help! Reach out to our friendly team with any questions 
              about our services, membership, or how we can support you.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle className="text-2xl">Send us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="firstName" className="block text-sm font-medium text-foreground mb-2">
                          First Name *
                        </label>
                        <Input
                          id="firstName"
                          placeholder="Enter your first name"
                          value={formData.firstName}
                          onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                          maxLength={100}
                          className={errors.firstName ? 'border-red-500' : ''}
                          required
                        />
                        {errors.firstName && (
                          <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
                        )}
                      </div>
                      <div>
                        <label htmlFor="lastName" className="block text-sm font-medium text-foreground mb-2">
                          Last Name *
                        </label>
                        <Input
                          id="lastName"
                          placeholder="Enter your last name"
                          value={formData.lastName}
                          onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                          maxLength={100}
                          className={errors.lastName ? 'border-red-500' : ''}
                          required
                        />
                        {errors.lastName && (
                          <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
                        Email Address *
                      </label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="Enter your email address"
                        value={formData.email}
                        onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                        maxLength={254}
                        className={errors.email ? 'border-red-500' : ''}
                        required
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground mb-2">
                        Phone Number
                      </label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="Enter your phone number"
                        value={formData.phone}
                        onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                        maxLength={20}
                        className={errors.phone ? 'border-red-500' : ''}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>
                    
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground mb-2">
                        Subject *
                      </label>
                      <Input
                        id="subject"
                        placeholder="What can we help you with?"
                        value={formData.subject}
                        onChange={(e) => setFormData(prev => ({ ...prev, subject: e.target.value }))}
                        maxLength={200}
                        className={errors.subject ? 'border-red-500' : ''}
                        required
                      />
                      {errors.subject && (
                        <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="topic" className="block text-sm font-medium text-foreground mb-2">
                        Topic (helps us route your message)
                      </label>
                      <Select value={formData.topic} onValueChange={(value) => setFormData(prev => ({ ...prev, topic: value }))}>
                        <SelectTrigger id="topic">
                          <SelectValue placeholder="Select a topic" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="general">General Question</SelectItem>
                          <SelectItem value="partner">Partnership</SelectItem>
                          <SelectItem value="sponsor">Sponsorship</SelectItem>
                          <SelectItem value="media">Press / Media</SelectItem>
                          <SelectItem value="sales">Sales / Demo</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground mb-2">
                        Message *
                      </label>
                      <Textarea
                        id="message"
                        placeholder="Tell us more about your question or how we can help..."
                        rows={6}
                        value={formData.message}
                        onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                        maxLength={2000}
                        className={errors.message ? 'border-red-500' : ''}
                        required
                      />
                      <div className="flex justify-between text-sm text-muted-foreground mt-1">
                        <span>{errors.message && <span className="text-red-500">{errors.message}</span>}</span>
                        <span>{formData.message.length}/2000</span>
                      </div>
                    </div>
                    
                    <Button type="submit" size="lg" className="w-full" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">Get in Touch</h2>
                <p className="text-muted-foreground mb-8">
                  Choose the best way to reach us. Our team is ready to assist you 
                  with any questions about our services.
                </p>
              </div>

              {contactInfo.map((info, index) => (
                <Card key={index}>
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0">
                        <info.icon className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-foreground mb-2">{info.title}</h3>
                        {info.details.map((detail, detailIndex) => (
                          <p key={detailIndex} className="text-sm text-muted-foreground">
                            {detail}
                          </p>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}

              {/* FAQ Link */}
              <Card className="bg-muted">
                <CardContent className="pt-6">
                  <h3 className="font-bold text-foreground mb-2">
                    Frequently Asked Questions
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Find quick answers to common questions about our services and membership.
                  </p>
                  <Link to="/support/faq">
                    <Button variant="outline" size="sm">
                      View FAQ
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Contact;
